import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, Plus } from "lucide-react";
import clsx from "clsx";
import * as api from "../../api/statoLavori";
import { getErrorMessage } from "../../api/client";
import { DispositivoDiSede } from "../../api/clients";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { AvviaPratica } from "./AvviaPratica";
import { AvanzamentoPratica, BozzePratica, FasePratica, SegnalazioniPratica, ServizioPratica } from "./PezziPratica";
import { FONTE_ATTIVAZIONE, TITOLO_BOZZA, dataIt, istanteIt, nomeCassaforte, ordinaPraticheSede } from "./presentazione";

// ============================================================================
// STATO LAVORI — riquadro della Vista Sede (issue #211, PR B4)
//
// Le pratiche con questa sede, una riga per cassaforte, come le mostra
// l'elenco (fase, avanzamento, servizio, segnalazioni), e il link alla scheda
// di ognuna. I passi si vedono solo per le pratiche avviate a mano: la sede
// già attivata senza pratica mostra solo la data di attivazione (Q1 e Q15).
//
// Lo monta `SedeDetail.tsx` solo per chi ha `stato_lavori:read`; «Avvia
// pratica» sta qui, e solo con `stato_lavori:update`. Si carica da sé
// (`GET /api/sedi/:id/stato-lavori`): se non risponde, il resto della scheda
// sede resta com'è.
//
// Le bozze da confermare delle sue casseforti (PR C2) vengono da
// `GET /api/lavori/bozze`, filtrate qui per le pratiche della sede. Se quella
// lettura non risponde, la riga dice solo quante sono (dal conteggio della pratica).
// ============================================================================

export function RiquadroSede({
  sedeId,
  clienteId,
  dispositivi,
  puoAvviare,
}: {
  sedeId: string;
  /** Il cliente della sede: senza, una pratica non si può avviare. */
  clienteId: string | null;
  dispositivi: DispositivoDiSede[];
  /** `stato_lavori:update`. */
  puoAvviare: boolean;
}) {
  const [dati, setDati] = useState<api.StatoLavoriSede | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [tentativo, setTentativo] = useState(0);
  const [avviaAperta, setAvviaAperta] = useState(false);
  const [bozze, setBozze] = useState<api.BozzaInElenco[] | null>(null);

  useEffect(() => {
    let annullato = false;
    setErrore(null);
    api
      .getStatoLavoriSede(sedeId)
      .then((d) => {
        if (!annullato) setDati(d);
      })
      .catch((err) => {
        if (!annullato) setErrore(getErrorMessage(err, "Impossibile caricare lo stato lavori della sede."));
      });
    return () => {
      annullato = true;
    };
  }, [sedeId, tentativo]);

  // Le bozze solo se la sede ha pratiche che ne hanno: niente lettura inutile.
  const conBozze = dati?.pratiche.some((p) => p.bozze_da_confermare > 0) ?? false;
  useEffect(() => {
    if (!conBozze) return;
    let annullato = false;
    api
      .getBozze()
      .then((b) => {
        if (!annullato) setBozze(b);
      })
      .catch(() => {
        if (!annullato) setBozze(null);
      });
    return () => {
      annullato = true;
    };
  }, [conBozze, sedeId, tentativo]);

  const pratiche = dati ? ordinaPraticheSede(dati.pratiche) : [];

  return (
    <section className="mb-8" aria-labelledby="stato-lavori-sede">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ListChecks className="h-4 w-4" aria-hidden="true" />
          </span>
          <h2 id="stato-lavori-sede" className="text-sm font-bold uppercase tracking-wide text-ink/70">
            Stato lavori
          </h2>
          {dati && dati.pratiche.length > 0 && <span className="text-sm text-ink/40">{dati.pratiche.length}</span>}
        </div>
        {puoAvviare && clienteId && (
          <Button variant="secondary" size="sm" onClick={() => setAvviaAperta(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Avvia pratica
          </Button>
        )}
      </div>

      {errore ? (
        <div className="flex flex-col items-center gap-3 rounded-card border border-line bg-white px-4 py-6 text-center shadow-card">
          <p className="text-sm text-[#b91c1c]">{errore}</p>
          <Button variant="secondary" size="sm" onClick={() => setTentativo((t) => t + 1)}>
            Riprova
          </Button>
        </div>
      ) : !dati ? (
        <p className="rounded-card border border-line bg-white px-4 py-6 text-center text-sm text-ink/50 shadow-card">Caricamento…</p>
      ) : pratiche.length === 0 ? (
        <div className="rounded-card border border-dashed border-line px-4 py-5 text-sm">
          {dati.attivata_il ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <Badge color="success">Attivata il {dataIt(dati.attivata_il.data)}</Badge>
              <span className="text-xs text-ink/55">{FONTE_ATTIVAZIONE[dati.attivata_il.fonte]}</span>
            </div>
          ) : (
            <p className="text-ink/60">Nessuna pratica avviata per questa sede.</p>
          )}
          <p className="mt-2 text-xs text-ink/55">
            I passi si seguono solo per le pratiche avviate a mano, una per cassaforte.
            {!clienteId && " Questa sede non ha un cliente: prima va collegata a uno."}
          </p>
        </div>
      ) : (
        <ul className="overflow-hidden rounded-card border border-line bg-white shadow-card">
          {pratiche.map((r) => (
            <RigaPraticaSede key={r.id} r={r} bozze={bozze ? bozze.filter((b) => b.pratiche.some((x) => x.id === r.id)) : null} />
          ))}
        </ul>
      )}

      {avviaAperta && clienteId && (
        <AvviaPratica sedeId={sedeId} clienteId={clienteId} dispositivi={dispositivi} onClose={() => setAvviaAperta(false)} />
      )}
    </section>
  );
}

/**
 * Una pratica della sede. Su schermo largo quattro colonne (cassaforte,
 * fase, avanzamento, segnalazioni); a larghezza telefono una sotto l'altra.
 */
function RigaPraticaSede({ r, bozze }: { r: api.RigaPratica; bozze: api.BozzaInElenco[] | null }) {
  const cassaforte = nomeCassaforte(r.cassaforte);
  // «Servizio da verificare» lo dice già il badge del servizio: qui non si ripete.
  const segnalazioni = r.segnalazioni.filter((s) => s.codice !== "servizio_da_verificare");
  return (
    <li
      className={clsx(
        "grid grid-cols-1 gap-3 border-b border-line px-4 py-3.5 text-sm last:border-b-0 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1fr)] md:gap-5",
        r.chiusa && "bg-black/[0.02]"
      )}
    >
      <div className="min-w-0">
        <p className="text-xs text-ink/55">Cassaforte</p>
        <p className="truncate font-semibold text-ink">{cassaforte}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <ServizioPratica servizio={r.servizio} testoDaVerificare="Servizio da verificare" />
        </div>
        <p className="mt-1.5 text-xs text-ink/55">
          Avviata il {istanteIt(r.avviata_il)}
          {r.installare_entro && ` · installare entro il ${dataIt(r.installare_entro)}`}
        </p>
        {/* Cassaforte condivisa: ogni cliente ha la sua pratica e la sua sede, i passi della cassaforte sono in comune. */}
        {r.condivisa_con.map((c) => (
          <p key={c.pratica_id} className="mt-1 text-xs text-ink/60">
            In comune con{" "}
            <Link to={`/stato-lavori/${c.pratica_id}`} className="font-semibold text-primary hover:underline">
              {c.cliente.nome}
              {c.sede.nome ? ` – ${c.sede.nome}` : ""}
            </Link>
          </p>
        ))}
      </div>
      <div className="min-w-0">
        <FasePratica fase={r.fase} />
      </div>
      <div className="min-w-0">
        <AvanzamentoPratica avanzamento={r.avanzamento} />
      </div>
      <div className="flex min-w-0 flex-col items-start gap-1">
        {(segnalazioni.length > 0 || r.bozze_da_confermare > 0) && (
          <div className="w-full">
            {bozze && bozze.length > 0 ? (
              bozze.map((b) => (
                <span key={b.id} className="mb-1 block rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                  <span className="font-semibold">{TITOLO_BOZZA[b.tipo]}</span> da confermare, dal {istanteIt(b.dal)}
                </span>
              ))
            ) : (
              <BozzePratica n={r.bozze_da_confermare} />
            )}
            <SegnalazioniPratica segnalazioni={segnalazioni} />
          </div>
        )}
        <Link
          to={`/stato-lavori/${r.id}`}
          aria-label={`Apri la pratica della cassaforte ${cassaforte}`}
          className="text-sm font-semibold text-primary hover:underline"
        >
          Apri la pratica
        </Link>
      </div>
    </li>
  );
}
